"use client";

import { motion, MotionValue, useTransform } from "motion/react";
import React from "react";
import { css } from "styled-system/css";

export interface CircularLoaderProps {
  /** diameter in px */
  size?: number;
  /** stroke width in px */
  thickness?: number;
  /** allows passing extra className */
  className?: string;
  /** progress from 0 to 1; if defined, overrides automatic spin */
  progress: MotionValue<number>;
  /** allows passing extra children to show in center */
  children?: React.ReactNode;
}

export function CircularLoader({
  size = 48,
  thickness = 4,
  className = "",
  progress,
  children,
}: CircularLoaderProps) {
  // Transform progress to scale (subtle pulse effect)
  const stroke = thickness;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  // Transform motion value to strokeDashoffset
  const dashOffset = useTransform(progress, [0, 1], [circumference, 0]);

  return (
    <div role="status" className={className}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={css({ transform: "rotate(-90deg)" })} // For the stroke to start from top
      >
        <circle
          r={radius}
          cx={size / 2}
          cy={size / 2}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.4}
          strokeWidth={stroke}
        />

        <motion.circle
          r={radius}
          cx={size / 2}
          cy={size / 2}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          style={{ strokeDashoffset: dashOffset }}
        />
      </svg>
      {children}
    </div>
  );
}
